import React, { useEffect, useState, useContext } from "react";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import { Button, Divider, Popover, Stack, Typography } from "@mui/material";
import IconButton from "@mui/material/IconButton";
import { Icon } from "../icon/icon.component";
import { withStyles } from "@mui/styles";
import CssBaseline from "@mui/material/CssBaseline";
import Badge from "@mui/material/Badge";
import cinchLogo from "../../assets/img/cinch-logo.svg";
import { UserStateContext } from "../context/user-state-context";
import { Tooltip } from "@mui/material";
import {
  AnalyticsIcon,
  AssetsFilledIcon,
  AssetsIcon,
  BroadcastsIcon,
  CarrotDownIcon,
  CarrotUpIcon,
  CouponFilledIcon,
  CouponIcon,
  CustomerGroupsFilledIcon,
  CustomerGroupsIcon,
  CustomersFilledIcon,
  CustomersIcon,
  DashboardFilledIcon,
  DashboardIcon,
  FileManagerFilledIcon,
  FileManagerIcon,
  HandymanIcon,
  ImportIcon,
  InboxFilledIcon,
  InboxIcon,
  InsertChartDataFilledIcon,
  InsertChartDataIcon,
  JourneyFilledIcon,
  JourneyIcon,
  LandingPagesIcon,
  ListAltFilledIcon,
  ListAltIcon,
  MarketingFilledIcon,
  MarketingIcon,
  NewspaperIcon,
  PriorityIcon,
  PriortyFilledIcon,
  RepeatTransactionCalculatorIcon,
  ReviewFilledIcon,
  ReviewIcon,
  SMSIcon,
  SegmentIcon,
  SidebarMenuCloseIcon,
  SidebarMenuOpenIcon,
  SmsFilledIcon,
  SurveyResponsesIcon,
  SurveysIcon,
  TemplatesFilledIcon,
  TemplatesIcon,
  TransactionFilledIcon,
  TransactionIcon,
  WebFormIcon,
  AppsIcon,
  AppsFilledIcon,
  StatusDotIcon,
} from "../mui/icon";
import { useNavigate } from "react-router-dom";

import moment from "moment-timezone";

const StyledButton = withStyles({
  root: {
    width: "56px",
    height: "40px",
    borderRadius: "20px",
    "&:hover": {
      backgroundColor: "#C1E6FE",
      color: "#000",
    },
  },
})(IconButton);

export const SidebarMui = () => {
  const navigate = useNavigate();
  const [sidebarExpanded, setSidebarExpanded] = useState(true);
  const [anchorEl, setAnchorEl] = useState(null);
  const [popoverData, setPopoverData] = useState(null);
  const [subMenuExpanded, setSubMenuExpanded] = useState([]);
  const {
    asCompany,
    hasPermission,

    user,
  } = useContext(UserStateContext);
  const [menu, setMenu] = useState([]);
  const [hasChildrenCompanies, setHasChildrenCompanies] = useState();
  const open = Boolean(anchorEl);

  useEffect(() => {
    if (localStorage.getItem("sidebarExpanded")) {
      setSidebarExpanded(localStorage.getItem("sidebarExpanded") === "true");
    }

    const resizeListener = () => {
      if (window.innerWidth < 1100) {
        setSidebarExpanded(false);
      }
    };
    window.addEventListener("resize", resizeListener);

    return () => {
      window.removeEventListener("resize", resizeListener);
    };
  }, []);

  useEffect(() => {
    if (subMenuExpanded.length == 0) {
      const path = window?.location?.pathname.split("/");
      const i = menu.filter((m) =>
        m.subMenu?.find((s) => s.key == "/" + path[1])
      );
      if (i.length > 0) {
        setSubMenuExpanded([...subMenuExpanded, i[0]]);
      }
    }
  }, [menu]);

  useEffect(() => {
    setHasChildrenCompanies(asCompany?.children?.length > 0);
  }, [asCompany]);

  const makeNavigationLink = (link) => {
    //turning off for now
    return `/${link}`;
  };

  useEffect(() => {
    const tempMenu = [
      {
        key: "/dashboard",
        name: "Dashboard",
        icon: DashboardIcon,
        iconFilled: DashboardFilledIcon,
      },
    ];

    tempMenu.push(
      {
        name: "Employee Portal",
        icon: InboxIcon,
        iconFilled: InboxFilledIcon,
        subMenu: [
          {
            key: "/request",
            name: "Request Company Access",
            icon: CustomersIcon,
            iconFilled: CustomersFilledIcon,
            // Gated to the same billing-admin tier as the route in
            // app-routes.js. Sourced from `internal_roles` on /users/me
            // (backend: `INTERNAL_ROLES["billing_admin"]`).
            enabled: !!user?.internal_roles?.includes("billing_admin"),
          },
        ],
      },

      {
        name: "Billing",
        icon: InsertChartDataIcon,
        iconFilled: InsertChartDataFilledIcon,
        subMenu: [
          {
            key: "/billing",
            name: "Billing Home",
            icon: CustomersIcon,
            iconFilled: CustomersFilledIcon,
            // Billing surface is billing-admin-only (matches
            // <RequireAccess access="billing-admin"> in app-routes.js
            // and the IsCinchBillingAdmin backend gate). Cinch employees
            // outside the whitelist shouldn't see the menu at all.
            enabled: !!user?.internal_roles?.includes("billing_admin"),
          },
          {
            key: "/billing/voice-agents",
            name: "Voice Agents",
            icon: CustomersIcon,
            iconFilled: CustomersFilledIcon,
            enabled: !!user?.internal_roles?.includes("billing_admin"),
          },
        ],
      },

      {
        name: "Release",
        icon: ListAltIcon,
        iconFilled: ListAltFilledIcon,
        subMenu: [
          {
            key: "/release-runbook",
            name: "Release Runbooks",
            icon: ListAltIcon,
            iconFilled: ListAltFilledIcon,
            // Engineer-only internal tool. Matches
            // <RequireAccess access="cinch-engineer"> in app-routes.js;
            // sourced from `internal_roles` on /users/me (backend:
            // `INTERNAL_ROLES["cinch_engineer"]`).
            enabled: !!user?.internal_roles?.includes("cinch_engineer"),
          },
        ],
      }
    );

    setMenu(tempMenu);
  }, [hasPermission, hasChildrenCompanies, user]);

  const handleExpandClick = (event) => {
    localStorage.setItem("sidebarExpanded", !sidebarExpanded);
    setSidebarExpanded(!sidebarExpanded);
  };

  const handleSupportClick = () => {
    window.open(user.support_url);
  };

  const checkChildrenPath = (item) => {
    if (!item || !window?.location?.pathname) return false;
    const path = window?.location?.pathname.split("/");
    if (
      item.key == window?.location?.pathname ||
      item.subMenu?.some((i) => i.key === "/" + path[1])
    )
      return true;
    if (item.subMenu) {
      return item.subMenu.some((subItem) => {
        return (
          subItem.subPages &&
          subItem.subPages.some(
            (s) => s == "/" + path[subItem?.subPagesDepthIndex || 1]
          )
        );
      });
    }
    return false;
  };

  const isSubItem = (subItem) => {
    const path = window?.location?.pathname.split("/");
    if ("/" + path[1] == subItem.key && !subItem?.subPagesDepthIndex) {
      return true;
    }

    //check if there are subitem matches, eg create-segment should match to segment
    if (subItem.subPages) {
      return !!subItem.subPages.some(
        (p) => p == "/" + path[subItem?.subPagesDepthIndex || 1]
      );
    }
    return false;
  };

  const handlePopoverOpen = (event, item) => {
    setPopoverData(item);
    setAnchorEl(event.currentTarget);
  };

  const handlePopoverClose = () => {
    setAnchorEl(null);
    setPopoverData(null);
  };

  return (
    <>
      <CssBaseline />
      <Box
        onMouseLeave={(e) => {
          handlePopoverClose();
        }}
        sx={{
          zIndex: 50,
          height: "100vh",
          width: "100vw",
          maxWidth: sidebarExpanded ? "360px" : "96px",
          boxShadow: "0px 0px 8px rgba(0, 0, 0, 0.05)",
        }}
      >
        {sidebarExpanded ? (
          <Paper
            className="d-flex column space-between secondary-color"
            sx={{
              flexDirection: "column",
              justifyContent: "space-between",
              width: "100%",
              height: "100vh",
              borderRadius: "0px",
              overflowY: "scroll",
              scrollbarWidth: "thin",
              "&::-webkit-scrollbar": {
                width: "0.4em",
              },
              "&::-webkit-scrollbar-track": {
                background: "#f1f1f1",
                marginTop: "20px",
                marginBottom: "20px",
              },
              "&::-webkit-scrollbar-thumb": {
                backgroundColor: "#888",
              },
              "&::-webkit-scrollbar-thumb:hover": {
                background: "#555",
              },
            }}
          >
            <Stack>
              <Box
                sx={{
                  display: "flex",
                  pt: "20px",
                  // pl: "36px",
                  pr: "7px",
                  marginBottom: "-15px",
                  marginLeft: "-25px",
                }}
                className="d-flex w-100 justify-content-between"
              >
                <Box
                  sx={{ display: "flex", alignItems: "center", width: "100%" }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      mb: 3,
                      // mr: 5,
                    }}
                  >
                    <img
                      src={cinchLogo}
                      alt="Cinch Logo"
                      style={{
                        color: "#66b6df",
                        width: "260px",
                        height: "28px",
                        display: "block",
                      }}
                    />
                  </Box>
                </Box>
                <Box sx={{ mt: 1 }}>
                  <Tooltip title="Collapse Sidebar">
                    <StyledButton
                      onClick={handleExpandClick}
                      sx={{
                        width: "24px",
                        height: "24px",
                        textAlign: "center",
                        padding: 0,
                        bottom: "8px",
                        color: "#1D252D",
                      }}
                    >
                      <SidebarMenuCloseIcon />
                    </StyledButton>
                  </Tooltip>
                </Box>
              </Box>
              <Stack sx={{ pt: "20px" }} spacing={1}>
                {menu
                  .filter(
                    (m) => !m.subMenu || m.subMenu?.some((s) => s.enabled)
                  )
                  .map((item, index) => (
                    <Box key={item.name} sx={{ pb: "10px", px: "20px" }}>
                      {item.name != "Support" && (
                        <Box
                          // to={item.key ? item.key : '#'}
                          onClick={(e) => {
                            if (item.key) {
                              navigate(item.key);
                            }
                          }}
                          sx={{
                            textDecoration: "none",
                            color: "#1D252D",
                            width: "100%",
                          }}
                        >
                          <Button
                            key={index}
                            onClick={(e) => {
                              if (
                                subMenuExpanded.some((i) => i.name == item.name)
                              ) {
                                setSubMenuExpanded(
                                  subMenuExpanded.filter(
                                    (i) => i.name != item.name
                                  )
                                );
                              } else if (item?.subMenu) {
                                setSubMenuExpanded([...subMenuExpanded, item]);
                              }
                            }}
                            fullWidth
                            sx={{
                              textAlign: "center",
                              color: "#1D252D",
                              background: checkChildrenPath(item)
                                ? "#3898D9"
                                : user.color_preference_secondary || "#fff",
                              textTransform: "none",
                              px: "16px",
                              justifyContent: "left",
                              alignContent: "center",
                              margin: "auto",
                              alignItems: "center",
                              height: "40px",
                              borderRadius: "20px",
                              "&:hover": {
                                backgroundColor: checkChildrenPath(item)
                                  ? "#3898D9"
                                  : "#C2E7FF",
                                color: "#53A6D6",
                              },
                            }}
                          >
                            <Stack
                              direction="row"
                              sx={{
                                display: "flex",
                                alignContent: "center",
                                justifyContent: "center",
                                justifyItems: "center",
                              }}
                              className="d-flex w-100 justify-content-between"
                            >
                              <Stack
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  alignContent: "center",
                                }}
                                direction="row"
                                spacing="20px"
                              >
                                <Badge
                                  badgeContent={item.badge}
                                  invisible={!item.badge}
                                  sx={{
                                    "& .MuiBadge-badge": {
                                      color: "#ffffff",
                                      backgroundColor:
                                        item?.badgeColor || "#E35989",
                                    },
                                  }}
                                >
                                  <>
                                    {checkChildrenPath(item) &&
                                    item.iconFilled ? (
                                      <item.iconFilled />
                                    ) : (
                                      <item.icon />
                                    )}
                                  </>
                                </Badge>
                                <Typography
                                  sx={{
                                    fontWeight: "700",
                                    fontSize: "14px",
                                    color: checkChildrenPath(item)
                                      ? "#FFFFFF"
                                      : "#1D252D",
                                  }}
                                >
                                  {item.name}
                                </Typography>
                              </Stack>
                              {item.subMenu &&
                                subMenuExpanded.find(
                                  (i) => i.name == item.name
                                ) && (
                                  <CarrotUpIcon
                                    fill={
                                      checkChildrenPath(item)
                                        ? "#FFFFFF"
                                        : "#1D252D"
                                    }
                                  />
                                )}
                              {item.subMenu &&
                                !subMenuExpanded.find(
                                  (i) => i.name == item.name
                                ) && (
                                  <CarrotDownIcon
                                    fill={
                                      checkChildrenPath(item)
                                        ? "#FFFFFF"
                                        : "#1D252D"
                                    }
                                  />
                                )}
                            </Stack>
                          </Button>
                        </Box>
                      )}
                      {subMenuExpanded.some((i) => i.name == item.name) && (
                        <Stack spacing={"10px"} sx={{ pt: "10px" }}>
                          {item.subMenu
                            ?.filter((sub) => sub.enabled)
                            .map((subItem) => (
                              <Box
                                key={subItem.key}
                                // to={subItem.key}
                                onClick={(e) => {
                                  if (
                                    window?.location?.pathname.split("?")[0] !=
                                    subItem?.key
                                  ) {
                                    navigate(subItem.key);
                                  }
                                }}
                              >
                                <Button
                                  sx={{
                                    borderRadius: "41px",
                                    width: "100%",
                                    justifyContent: "flex-start",
                                    "&:hover": {
                                      backgroundColor: "#C1E6FE",
                                      color: "#1D252D",
                                    },
                                  }}
                                >
                                  <Box
                                    sx={{
                                      width: "100%",
                                      display: "flex",
                                      justifyContent: "space-between",
                                    }}
                                  >
                                    <Stack
                                      sx={{
                                        pl: "18px",
                                        py: "3px",
                                        color: "inherit",
                                      }}
                                      direction="row"
                                    >
                                      <>
                                        {isSubItem(subItem) ? (
                                          <Box
                                            sx={{
                                              pr: "5px",
                                              borderLeft: "2px solid",
                                              borderColor: "#53A6D6",
                                            }}
                                          />
                                        ) : (
                                          <Box
                                            sx={{
                                              pr: "7px",
                                            }}
                                          />
                                        )}

                                        {window?.location?.pathname ===
                                          subItem.key && subItem.iconFilled ? (
                                          <Badge
                                            invisible={!subItem.badge}
                                            badgeContent={subItem.badge}
                                            sx={{
                                              "& .MuiBadge-badge": {
                                                color: "#ffffff",
                                                backgroundColor:
                                                  subItem?.badgeColor ||
                                                  "#E35989",
                                              },
                                            }}
                                          >
                                            <subItem.iconFilled
                                              fill={
                                                isSubItem(subItem)
                                                  ? "#53A6D6"
                                                  : "#1D252D"
                                              }
                                            />
                                          </Badge>
                                        ) : (
                                          <Badge
                                            invisible={!subItem.badge}
                                            badgeContent={subItem.badge}
                                            sx={{
                                              "& .MuiBadge-badge": {
                                                color: "#ffffff",
                                                backgroundColor:
                                                  subItem?.badgeColor ||
                                                  "#E35989",
                                              },
                                            }}
                                          >
                                            <subItem.icon
                                              fill={
                                                isSubItem(subItem)
                                                  ? "#53A6D6"
                                                  : "#1D252D"
                                              }
                                            />
                                          </Badge>
                                        )}
                                      </>

                                      {subItem?.indent && (
                                        <Box
                                          sx={{
                                            pr: "14px",
                                          }}
                                        />
                                      )}
                                      {subItem?.warnStatus && (
                                        <Box sx={{ mt: "2px" }}>
                                          <StatusDotIcon fill="#FFB82B" />
                                        </Box>
                                      )}

                                      <Typography
                                        sx={{
                                          fontSize: 14,
                                          fontWeight: "700",
                                          pl: subItem.warnStatus
                                            ? "0px"
                                            : "20px",
                                          mt: "5px",
                                          color: isSubItem(subItem)
                                            ? "#53A6D6"
                                            : "#1D252D",
                                          textTransform: "none",
                                        }}
                                      >
                                        {subItem.name}
                                      </Typography>
                                    </Stack>
                                  </Box>
                                </Button>
                              </Box>
                            ))}
                        </Stack>
                      )}
                    </Box>
                  ))}
              </Stack>
            </Stack>
            <Stack
              spacing={1}
              sx={{
                display: "flex",
                alignItems: "left",
                textAlign: "center",
                pb: "13px",
                justifyContent: "left",
              }}
            >
              <Box sx={{ position: "relative", bottom: "6px" }}>
                <Divider sx={{ color: "#EDEFF1" }} flexItem />
              </Box>

              <Box
                sx={{
                  position: "relative",
                  top: "2px",
                  right: "0px",
                  px: "20px",
                }}
              >
                <Button
                  onClick={handleSupportClick}
                  fullWidth
                  sx={{
                    textAlign: "center",
                    height: "40px",
                    padding: 0,
                    justifyContent: "left",
                    color: "#1D252D",
                    borderRadius: "20px",
                    "&:hover": {
                      backgroundColor: "#C1E6FE",
                    },
                  }}
                >
                  <Stack direction="row" sx={{ pl: "16px" }}>
                    <Icon name="custom-headset_mic" size={24} />
                    <Typography
                      variant="tableHeader"
                      sx={{
                        position: "relative",
                        top: "4px",
                        pl: "20px",
                        textTransform: "none",
                      }}
                    >
                      Support
                    </Typography>
                  </Stack>
                </Button>
              </Box>
            </Stack>
          </Paper>
        ) : (
          <Paper
            className="d-flex column space-between secondary-color"
            sx={{
              flexDirection: "column",
              justifyContent: "space-between",
              width: "100%",
              height: "100vh",
              borderRadius: "0px",
            }}
          >
            <Stack
              spacing={1}
              sx={{
                display: "flex",
                alignItems: "center",
                textAlign: "center",
                alignContent: "center",
                pt: "13px",
                justifyContent: "space-between",
              }}
            >
              <Box onMouseEnter={handlePopoverClose} sx={{ pb: "27px" }}>
                <Tooltip title="Expand Sidebar">
                  <StyledButton
                    onClick={handleExpandClick}
                    sx={{
                      width: "24px",
                      height: "24px",
                      textAlign: "center",
                      padding: 0,
                      color: "#1D252D",
                    }}
                  >
                    <SidebarMenuOpenIcon />
                  </StyledButton>
                </Tooltip>
              </Box>
              {menu
                .filter((m) => !m.subMenu || m.subMenu?.some((s) => s.enabled))
                .map((item, index) => (
                  <div
                    key={index}
                    style={{ marginTop: "0px", paddingBottom: "18px" }}
                  >
                    {item.name != "Support" && (
                      <IconButton
                        onClick={(e) => {
                          if (item.key == "/dashboard") {
                            navigate(item.key);
                          }
                        }}
                        onMouseEnter={(e) => {
                          handlePopoverOpen(e, item);
                        }}
                        sx={{
                          width: "56px",
                          height: "40px",
                          textAlign: "center",
                          padding: 0,
                          color: "#1D252D",
                          background: checkChildrenPath(item)
                            ? "#3898D9"
                            : user.color_preference_secondary || "#fff",
                          borderRadius: "20px",
                          "&:hover": {
                            backgroundColor: checkChildrenPath(item)
                              ? "#3898D9"
                              : "#C2E7FF",
                            color: "#000",
                          },
                        }}
                      >
                        <Badge
                          badgeContent={item.badge}
                          invisible={!item?.badge}
                          sx={{
                            "& .MuiBadge-badge": {
                              color: "#ffffff",
                              backgroundColor: item?.badgeColor || "#E35989",
                            },
                          }}
                        >
                          <>
                            {checkChildrenPath(item) && item.iconFilled ? (
                              <item.iconFilled />
                            ) : (
                              <item.icon />
                            )}
                          </>
                        </Badge>
                      </IconButton>
                    )}
                  </div>
                ))}
            </Stack>
            <Box
              onMouseEnter={handlePopoverClose}
              sx={{ height: "100%" }}
            ></Box>
            <Stack
              spacing={2}
              sx={{
                display: "flex",
                alignItems: "center",
                textAlign: "center",
                pb: "13px",
              }}
            >
              <Divider flexItem sx={{ px: 2 }} />

              <StyledButton
                onClick={handleSupportClick}
                onMouseEnter={(e) => {
                  handlePopoverOpen(e, { name: "Support" });
                }}
                sx={{
                  width: "24px",
                  height: "24px",
                  textAlign: "center",
                  padding: 0,
                  color: "#1D252D",
                }}
              >
                <Icon name="custom-headset_mic" size={24} />
              </StyledButton>
            </Stack>
          </Paper>
        )}

        <Popover
          id="mouse-over-popover"
          sx={{
            pointerEvents: "none",
            zIndex: 200,
          }}
          open={open}
          anchorEl={anchorEl}
          anchorOrigin={{
            vertical: 0,
            horizontal: 60,
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "left",
          }}
          onClose={handlePopoverClose}
          PaperProps={{
            style: {
              width: "320px",
              borderRadius: "20px",
              boxShadow: "0px 0px 8px rgba(0, 0, 0, 0.05)",
            },
          }}
          disableRestoreFocus
        >
          <Box
            display="flex"
            flexDirection="row"
            onMouseLeave={handlePopoverClose}
            sx={{
              pointerEvents: "auto",
              zIndex: 200,
              cursor: "pointer",
            }}
          >
            <Stack sx={{ width: "100%" }}>
              <Button
                variant="text"
                fullWidth
                onClick={(e) => {
                  if (popoverData?.name == "Support") {
                    handleSupportClick();
                  }
                }}
                sx={{
                  borderRadius: "20px",
                  height: "40px",
                  cursor: "pointer",
                  color: "#1D252D",
                  textTransform: "none",
                  justifyContent: "flex-start",
                  backgroundColor: checkChildrenPath(popoverData)
                    ? "#3898D9"
                    : "#C1E6FE",
                  "&:hover": {
                    backgroundColor: checkChildrenPath(popoverData)
                      ? "#3898D9"
                      : "#C1E6FE",
                  },
                }}
              >
                <Box
                  // to={popoverData?.key ? popoverData.key : '#'}
                  onClick={(e) => {
                    if (popoverData?.key) {
                      navigate(popoverData.key);
                    }
                  }}
                  sx={{
                    textDecoration: "none",
                    color: "#1D252D",
                    width: "100%",
                  }}
                >
                  <Typography
                    variant="tableHeader"
                    sx={{
                      fontSize: 14,
                      pl: "24px",
                      mt: "5px",
                      display: "flex",
                      alignItems: "center",
                      textAlign: "center",
                      alignContent: "center",
                      bottom: "2px",
                      position: "relative",
                      color: checkChildrenPath(popoverData)
                        ? "#FFFFFF"
                        : "#1D252D",
                    }}
                  >
                    {popoverData?.name}
                  </Typography>
                </Box>
              </Button>
              <Box
                sx={{ py: popoverData?.subMenu?.length > 0 ? "20px" : "0px" }}
              >
                <Stack spacing={1}>
                  {popoverData?.subMenu
                    ?.filter((sub) => sub.enabled)
                    .map((subItem, index) => (
                      <Box
                        key={subItem.key}
                        //to={subItem.key}
                        onClick={(e) => {
                          if (
                            window?.location?.pathname.split("?")[0] !=
                            subItem?.key
                          ) {
                            navigate(subItem.key);
                            handlePopoverClose();
                          }
                        }}
                        sx={{
                          textDecoration: "none",
                          color: "inherit",
                          width: "100%",
                          height: "100%",
                        }}
                      >
                        <Button
                          variant="text"
                          fullWidth
                          key={subItem.key + index}
                          sx={{
                            cursor: "pointer",
                            textTransform: "none",
                            justifyContent: "flex-start",
                            width: "100%",
                            height: "40px",
                            borderRadius: "20px",
                            color:
                              window?.location?.pathname === subItem.key
                                ? "#53A6D6"
                                : "#1D252D",
                            // borderLeft: '2px solid',
                            borderColor:
                              window?.location?.pathname === subItem.key
                                ? "#53A6D6"
                                : "#EDEFF1",
                            background: "#fff",
                            "&:hover": {
                              backgroundColor: "#C2E7FF",
                              color: "#53A6D6",
                            },
                          }}
                        >
                          <Stack
                            sx={{ pl: "30px", color: "inherit" }}
                            direction="row"
                          >
                            <>
                              {isSubItem(subItem) ? (
                                <Box
                                  sx={{
                                    pr: "5px",
                                    borderLeft: "2px solid",
                                    borderColor: "#53A6D6",
                                  }}
                                />
                              ) : (
                                <Box
                                  sx={{
                                    pr: "7px",
                                  }}
                                />
                              )}

                              {window?.location?.pathname === subItem.key &&
                              subItem.iconFilled ? (
                                <Badge
                                  invisible={!subItem.badge}
                                  badgeContent={subItem.badge}
                                  sx={{
                                    "& .MuiBadge-badge": {
                                      color: "#ffffff",
                                      backgroundColor:
                                        subItem?.badgeColor || "#E35989",
                                    },
                                  }}
                                >
                                  <subItem.iconFilled
                                    fill={
                                      isSubItem(subItem) ? "#53A6D6" : "#1D252D"
                                    }
                                  />
                                </Badge>
                              ) : (
                                <Badge
                                  invisible={!subItem.badge}
                                  badgeContent={subItem.badge}
                                  sx={{
                                    "& .MuiBadge-badge": {
                                      color: "#ffffff",
                                      backgroundColor:
                                        subItem?.badgeColor || "#E35989",
                                    },
                                  }}
                                >
                                  <subItem.icon
                                    fill={
                                      isSubItem(subItem) ? "#53A6D6" : "#1D252D"
                                    }
                                  />
                                </Badge>
                              )}
                            </>

                            {subItem?.indent && (
                              <Box
                                sx={{
                                  pr: "14px",
                                }}
                              />
                            )}
                            {subItem?.warnStatus && (
                              <Box sx={{ mt: "2px" }}>
                                <StatusDotIcon fill="#FFB82B" />
                              </Box>
                            )}

                            <Typography
                              variant="tableHeader"
                              sx={{
                                fontSize: 14,
                                pl: subItem?.warnStatus ? "0px" : "24px",
                                mt: "5px",
                                color: isSubItem(subItem)
                                  ? "#53A6D6"
                                  : "#1D252D",
                              }}
                            >
                              {subItem.name}
                            </Typography>
                            {subItem?.badge && (
                              <Typography
                                flexGrow="1"
                                variant="tableHeader"
                                sx={{
                                  fontSize: 14,
                                  pr: "14px",
                                  mt: "5px",
                                  color: "inherit",
                                  textAlign: "right",
                                }}
                              >
                                {subItem.badge}
                              </Typography>
                            )}
                          </Stack>
                        </Button>
                      </Box>
                    ))}
                </Stack>
              </Box>
            </Stack>
          </Box>
        </Popover>
      </Box>
    </>
  );
};
